import {
  featuresOf,
  type Entitlements,
  type FeatureCapability,
  type OptionSchema,
  type Tier
} from '@chartforge/shared-types';

/**
 * Centralized entitlement gate.
 *
 * This is the SINGLE source of truth for customization gating. Feature checks
 * such as `if (isPaid)` must never be scattered through charts, UI or adapters —
 * everything flows through schema tiers filtered here.
 *
 * ## M4: tiers *and* capabilities
 *
 * The PRD keeps `OptionSchema.tier` as the mechanism and asks for capability
 * checks rather than hard-coded plan checks. Both hold here:
 *
 * ```text
 * OptionSchema { tier, features? }
 *        │
 *        ▼
 * filterSchemasByEntitlement(schemas, entitlements)
 *        │   tier rank    : plan ≥ schema.tier
 *        │   capability   : every schema feature ∈ capability set
 *        ▼
 * available customization
 * ```
 *
 * The capability set is `featuresOf(entitlements)`, which **intersects** the
 * token's declared capabilities with the plan baseline
 * (`@chartforge/shared-types`). A token therefore cannot unlock more than its
 * plan allows, and a legacy `{ plan }` entitlement keeps its exact M0–M3
 * behaviour — the tier is still the primary gate, so no existing schema or test
 * changes meaning.
 */
const TIER_RANK: Record<Tier, number> = {
  trial: 0,
  basic: 1,
  pro: 2
};

export function tierRank(tier: Tier): number {
  return TIER_RANK[tier];
}

/**
 * The capabilities an entitlement actually grants (plan-narrowed).
 *
 * Exported so hosts never re-derive it: the watermark, the UI kit and the
 * adapters all read the same value.
 */
export function capabilitySet(entitlements: Entitlements): FeatureCapability[] {
  return featuresOf(entitlements);
}

/** True when the entitlement grants a capability. */
export function hasCapability(entitlements: Entitlements, capability: FeatureCapability): boolean {
  return capabilitySet(entitlements).includes(capability);
}

/** What a schema still needs before it can be shown. Empty ⇒ it can be shown. */
export interface MissingRequirements {
  /** True when the plan's rank is below the schema's tier. */
  tier: boolean;
  /** Capabilities the entitlement does not grant. */
  features: FeatureCapability[];
}

/**
 * Diagnostic detail for a gated schema.
 *
 * Used by host diagnostics ("why is this option missing?") — never as a
 * second gate: `filterSchemasByEntitlement` and `isEntitled` both call this, so
 * the answer cannot disagree with the filter.
 */
export function missingRequirements(
  schema: OptionSchema,
  entitlements: Entitlements
): MissingRequirements {
  const capabilities = capabilitySet(entitlements);
  return {
    tier: tierRank(entitlements.plan) < tierRank(schema.tier),
    features: (schema.features ?? []).filter((feature) => !capabilities.includes(feature))
  };
}

/** True when a schema may be shown/edited under these entitlements. */
export function isEntitled(schema: OptionSchema, entitlements: Entitlements): boolean {
  const missing = missingRequirements(schema, entitlements);
  return !missing.tier && missing.features.length === 0;
}

/** The available customization for a plan: the whole point of the gate. */
export function filterSchemasByEntitlement(
  schemas: OptionSchema[],
  entitlements: Entitlements
): OptionSchema[] {
  return schemas.filter((schema) => isEntitled(schema, entitlements));
}

/**
 * Ids of the schemas an entitlement does *not* grant.
 *
 * Hosts use this to explain an upgrade ("Pro adds 3 options") without
 * re-implementing any gating logic.
 */
export function lockedSchemaIds(schemas: OptionSchema[], entitlements: Entitlements): string[] {
  return schemas.filter((schema) => !isEntitled(schema, entitlements)).map((schema) => schema.id);
}
